<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-2">
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="primary">mdi-bullhorn</v-icon>
        <span class="text-h5 font-weight-bold">Consideration Analysis</span>
      </div>
      <v-card v-if="hasData && personaOptions.length > 0" variant="outlined" class="metric-card d-inline-block flex-shrink-0" style="min-width: 140px;">
        <v-card-text class="text-center pa-3">
          <div class="text-h4 font-weight-bold" :class="getSentimentColorClass(toneOfSummaryScore)">
            {{ toneOfSummaryScore.toFixed(1) }}
          </div>
          <div class="text-subtitle-2 font-weight-bold mt-1 d-flex align-center justify-center">
            Sentiment
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="small" color="grey" class="ml-1">mdi-information-outline</v-icon>
              </template>
              <span>Overall tone of AI responses: positive, neutral, and negative sentiment scaled to 0–100</span>
            </v-tooltip>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
  <div class="pa-6">
    <!-- Persona filter hidden for now (functionality preserved) -->
    <v-row v-if="false" class="mb-4 align-center" justify="space-between" no-gutters>
      <v-col cols="12" md="4" class="pr-2">
        <v-select
          v-model="selectedPersona"
          :items="personaOptions"
          label="Filter by Persona"
          variant="outlined"
          density="comfortable"
          hide-details
        ></v-select>
      </v-col>
    </v-row>
    <v-row v-if="!hasData" class="text-center py-8">
      <v-col cols="12">
        <v-alert type="info" variant="tonal">
          No query data available for consideration analysis.
        </v-alert>
      </v-col>
    </v-row>

    <template v-else>
      <HistoricalDateBanner
        :visible="showSnapshotBanner"
        :loading="loadingSnapshot"
        :load-target="snapshotLoadTarget"
        :selected-date="selectedHistoricalDate"
        :error="snapshotError"
        @clear="$emit('historical-date-cleared')"
      />

      <!-- Metrics Overview (Accuracy Score commented out) -->
      <!-- <v-row class="mb-6">
        <v-col cols="12" md="4">
          <v-card variant="outlined" class="metric-card">
            <v-card-text class="text-center pa-6">
              <div class="text-h3 font-weight-bold mb-2" :class="getAccuracyScoreColorClass(accuracyScore)">
                {{ accuracyScore.toFixed(1) }}%
              </div>
              <div class="text-subtitle-1 font-weight-bold">Accuracy Score</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row> -->

      <!-- Historical Sentiment Chart -->
      <v-row class="mb-6">
        <v-col cols="12">
          <div v-if="lacksHistoricalTrendAccess" class="upgrade-gate-banner brand-gate-card mb-4">
            <div class="d-flex align-center justify-space-between flex-wrap ga-3">
              <div>
                <div class="font-weight-bold mb-1 brand-gate-card__title">To see historical data over time</div>
                <div class="text-body-2 brand-gate-card__body">
                  Upgrade to unlock trend charts and ongoing tracking.
                </div>
              </div>
              <v-btn
                variant="flat"
                class="brand-upgrade-cta-btn text-none font-weight-bold"
                @click="$router.push('/profile?tab=billing')"
              >
                Upgrade Your Plan
              </v-btn>
            </div>
          </div>
          <v-card variant="outlined" class="historical-chart-card">
            <v-card-title class="d-flex flex-column align-center justify-center">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
                Sentiment Over Time
              </div>
              <div v-if="currentHistoricalData.length > 1" class="text-caption text-medium-emphasis font-weight-regular mt-1">
                Click a data point to view scores for that date
              </div>
            </v-card-title>
            <v-card-text>
              <div v-if="currentHistoricalData.length <= 1" class="text-center py-12">
                <v-icon color="grey" size="64" class="mb-4">mdi-chart-line-variant</v-icon>
                <div class="text-h6 mb-2 text-medium-emphasis">Data not available</div>
                <div class="d-flex align-center justify-center">
                  <v-icon color="info" size="20" class="mr-2">mdi-information</v-icon>
                  <span class="text-body-2 text-medium-emphasis">
                    Historical data is being collected, this takes ~2 days to start tracking historically.
                  </span>
                </div>
              </div>
              <div v-else id="sentimentChart" style="height: 300px;"></div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Competitor Comparison Section (only show if comparison data exists) -->
      <v-row v-if="comparisonData" class="mb-6">
        <v-col cols="12">
          <v-alert
            v-if="selectedHistoricalDate"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            Competitor comparison reflects the original assessment, not related to the selected date.
          </v-alert>
          <v-card variant="outlined">
            <v-card-title class="pa-6">
              <v-icon class="mr-3" color="primary">mdi-chart-bar-stacked</v-icon>
              Competitor Comparison
            </v-card-title>
            <v-card-text>
              <highcharts :options="radarChartOptions"></highcharts>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Attribute Sentiment Matrix Section (only show if comparison data exists) -->
      <v-row v-if="comparisonData && attributeSentimentData.length > 0" class="mb-6">
        <v-col cols="12">
          <v-card variant="outlined">
            <v-card-title class="pa-6 d-flex align-center justify-space-between flex-wrap">
              <div class="d-flex flex-column">
                <div class="d-flex align-center">
                  <v-icon class="mr-3" color="primary">mdi-table-large</v-icon>
                  Attribute Sentiment Matrix
                  <v-tooltip
                    v-if="showCitationsTooltip"
                    location="top"
                    max-width="280"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="grey" class="ml-2">mdi-information-outline</v-icon>
                    </template>
                    <span>Gemini grounding didn't return per-claim source mappings, the model relied on training data due to high confindence.</span>
                  </v-tooltip>
                </div>
                <div class="matrix-legend">
                  <template v-if="sentimentMatrixMode === 'comparative'">
                    <span class="matrix-legend-label">Comparative:</span>
                    <div class="matrix-legend-items">
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-positive"></span>1st
                      </span>
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-neutral"></span>2nd
                      </span>
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-yellow"></span>3rd
                      </span>
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-orange"></span>4th
                      </span>
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-negative"></span>5th+
                      </span>
                    </div>
                  </template>
                  <template v-else>
                    <span class="matrix-legend-label">Absolute:</span>
                    <div class="matrix-legend-items">
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-positive"></span>10 out of 10!
                      </span>
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-neutral"></span>9+
                      </span>
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-yellow"></span>8+
                      </span>
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-orange"></span>7+
                      </span>
                      <span class="matrix-legend-item">
                        <span class="matrix-legend-swatch matrix-legend-swatch-negative"></span>less than 7
                      </span>
                    </div>
                  </template>
                </div>
              </div>
              <div class="d-flex align-center">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" size="small" color="grey" class="mr-2">mdi-information-outline</v-icon>
                  </template>
                  <span>Comparative: colour-coded by rank. <br> Absolute: score band (e.g. 10 = green, &lt;7 = red).</span>
                </v-tooltip>
                <v-btn-toggle
                  v-model="sentimentMatrixMode"
                  mandatory
                  density="compact"
                  variant="outlined"
                  divided
                >
                  <v-btn value="comparative" size="small">
                    Comparative
                  </v-btn>
                  <v-btn value="absolute" size="small">
                    Absolute
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-card-title>
            <v-card-text class="pa-0">
              <div class="attribute-sentiment-table-container">
                <table class="attribute-sentiment-table">
                  <thead>
                    <tr>
                      <th class="brand-column">Brand - Product</th>
                      <th 
                        v-for="metric in attributeSentimentMetrics" 
                        :key="metric.key"
                        class="metric-column"
                      >
                        {{ metric.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(product, productIndex) in attributeSentimentData"
                      :key="product.name"
                      :class="{
                        'primary-product-row': product.is_primary,
                        'matrix-row-blurred': shouldBlurMatrixRow(product, productIndex)
                      }"
                    >
                      <td :class="['brand-cell', { 'primary-brand-cell': product.is_primary }]">
                        <span class="font-weight-medium">
                          {{ product.is_primary && primaryBrandName ? `${primaryBrandName} - ${product.name}` : product.name }}
                        </span>
                      </td>
                      <td 
                        v-for="metric in attributeSentimentMetrics" 
                        :key="`${product.name}-${metric.key}`"
                        class="reasoning-cell"
                      >
                        <div class="reasoning-items-container">
                          <div 
                            v-for="(reason, index) in getProductReasoning(product, metric.key)" 
                            :key="index"
                            class="reasoning-card-wrapper"
                          >
                            <div
                              :class="[getReasoningCardClass(product, metric.key), { 'reasoning-card-grounded': reason.citation_urls && reason.citation_urls.length > 0 }]"
                              class="reasoning-card"
                              @click="reason.citation_urls && reason.citation_urls.length > 0 && toggleCitation(`${product.name}-${metric.key}-${index}`)"
                            >
                              <span 
                                :class="getReasoningDotClass(product, metric.key)"
                                class="reasoning-dot"
                              ></span>
                              <span
                                class="reasoning-text"
                                :class="{ 'brand-paywall-blur': shouldBlurMatrixRow(product, productIndex) }"
                              >{{ reason.text }}</span>
                              <v-icon 
                                v-if="reason.citation_urls && reason.citation_urls.length > 0"
                                size="x-small" 
                                color="success" 
                                class="ml-auto reasoning-grounded-icon"
                              >
                                {{ expandedCitations[`${product.name}-${metric.key}-${index}`] ? 'mdi-chevron-up' : 'mdi-link-variant' }}
                              </v-icon>
                              <span
                                v-if="getMetricDisplayValue(product, metric.key) != null"
                                class="reasoning-card-metric-value"
                                :class="{ 'brand-paywall-blur': shouldBlurMatrixRow(product, productIndex) }"
                              >
                                {{ getMetricDisplayValue(product, metric.key) }}
                              </span>
                            </div>
                            <!-- Expanded citation details -->
                            <div 
                              v-if="reason.citation_urls && reason.citation_urls.length > 0 && expandedCitations[`${product.name}-${metric.key}-${index}`]"
                              :class="getCitationDetailsClass(product, metric.key)"
                              class="citation-details"
                            >
                              <div class="citation-header">
                                <v-icon size="x-small" class="mr-1">mdi-check-decagram</v-icon>
                                <span>LLM verified from:</span>
                              </div>
                              <!-- Show snippet once -->
                              <div v-if="reason.citation_urls[0]?.snippet" class="citation-snippet">
                                <v-icon size="x-small" class="mr-1">mdi-format-quote-open</v-icon>
                                {{ truncateText(reason.citation_urls[0].snippet, 120) }}
                              </div>
                              <!-- List all citation URLs -->
                              <div class="citation-links">
                                <a 
                                  v-for="(link, linkIdx) in reason.citation_urls"
                                  :key="linkIdx"
                                  :href="link.url"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="citation-source-link"
                                  @click.stop
                                >
                                  <v-icon size="x-small" class="mr-1">mdi-open-in-new</v-icon>
                                  {{ truncateText(link.title, 50) }}
                                  <v-tooltip
                                    v-if="link.validated"
                                    location="top"
                                  >
                                    <template v-slot:activator="{ props }">
                                      <v-icon
                                        v-bind="props"
                                        size="x-small"
                                        color="success"
                                        class="ml-1"
                                      >
                                        mdi-check-decagram
                                      </v-icon>
                                    </template>
                                    <span>Verified URL</span>
                                  </v-tooltip>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-if="!getProductReasoning(product, metric.key).length" class="text-grey">
                          —
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-expansion-panels v-model="expansionPanel" class="mt-4" variant="accordion">
        <v-expansion-panel>
          <v-expansion-panel-title>
            <v-icon class="mr-2" color="primary">mdi-chart-bar</v-icon>
            Detailed Breakdown
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-table>
              <thead>
                <tr>
                  <th class="text-left">Query</th>
                  <!-- <th class="text-center">Brand Alignment</th> -->
                  <th class="text-center">Sentiment Analysis</th>
                  <!-- <th class="text-center">Citation Score</th> -->
                </tr>
              </thead>
              <tbody>
                <template v-for="(personaGroup, personaIndex) in queriesByPersona" :key="personaIndex">
                  <tr class="persona-header-row">
                    <td colspan="2" class="font-weight-bold text-primary py-2">
                      {{ getPersonaName(personaIndex) }}
                    </td>
                  </tr>
                  <tr v-for="query in personaGroup.queries" :key="query.id">
                    <td>
                      <div class="font-weight-medium">{{ query.query_text }}</div>
                    </td>
                    <!-- <td class="text-center">
                      <v-chip 
                        :color="query.brandAlignment ? 'success' : 'error'"
                        size="small"
                      >
                        {{ query.brandAlignment ? 'Aligned' : 'Not Aligned' }}
                      </v-chip>
                    </td> -->
                    <td class="text-center">
                      <v-chip 
                        :color="getSentimentChipColor(query.sentiment)"
                        size="small"
                      >
                        {{ getSentimentLabel(query.sentiment) }}
                      </v-chip>
                    </td>
                    <!-- <td class="text-center">
                      <span class="text-grey">—</span>
                    </td> -->
                  </tr>
                </template>
              </tbody>
            </v-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </div>
</template>

<script>
import axios from '@/axios.js'
import Highcharts from 'highcharts'
import HighchartsMore from 'highcharts/highcharts-more'
import { Chart } from 'highcharts-vue'
import { mapGetters } from 'vuex'
import HistoricalDateBanner from '@/components/HistoricalDateBanner.vue'
import { filterHistoricalPayload, formatHistoricalDate, isWithinHistoricalWindow } from '@/utils/historicalData'

// Enable Highcharts More module for polar charts
HighchartsMore(Highcharts)

const BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  name: 'ConsiderationTab',
  components: {
    highcharts: Chart,
    HistoricalDateBanner
  },
  props: {
    queries: {
      type: Array,
      default: () => []
    },
    personaProfiles: {
      type: Array,
      default: () => []
    },
    reviewEntityId: {
      type: Number,
      default: null
    },
    selectedProvider: {
      type: String,
      default: null
    },
    assessmentId: {
      type: Number,
      default: null
    },
    primaryBrandName: {
      type: String,
      default: null
    },
    selectedHistoricalDate: {
      type: String,
      default: null
    },
    loadingSnapshot: {
      type: Boolean,
      default: false
    },
    snapshotLoadTarget: {
      type: String,
      default: null
    },
    showSnapshotBanner: {
      type: Boolean,
      default: false
    },
    snapshotError: {
      type: String,
      default: null
    }
  },
  emits: ['historical-date-selected', 'historical-date-cleared'],
  data() {
    return {
      selectedPersona: 'all',
      historicalData: [],
      historicalDataByPersona: [],
      loadingHistorical: false,
      chartOptions: null,
      expansionPanel: [0],
      comparisonData: null,
      loadingComparison: false,
      expandedCitations: {},
      sentimentMatrixMode: 'comparative'
    }
  },
  computed: {
    ...mapGetters(['lacksHistoricalTrendAccess', 'isFreeTierPlan']),
    personaOptions() {
      const options = [{ title: 'All', value: 'all' }]
      if (this.personaProfiles && this.personaProfiles.length > 0) {
        this.personaProfiles.forEach((persona, index) => {
          options.push({
            title: persona.name || `Persona ${index + 1}`,
            value: index
          })
        })
      }
      return options
    },
    filteredQueries() {
      if (this.selectedPersona === 'all') {
        return this.queries
      }
      // Filter queries by persona index (3 queries per persona)
      const personaIndex = parseInt(this.selectedPersona)
      const startIndex = personaIndex * 3
      const endIndex = startIndex + 3
      return this.queries.slice(startIndex, endIndex)
    },
    hasData() {
      return this.queries && this.queries.length > 0
    },
    isGroundedGeminiProvider() {
      const p = (this.selectedProvider || '').toLowerCase()
      return p === 'google_grounded' || p === 'gemini_grounded'
    },
    showCitationsTooltip() {
      if (!this.isGroundedGeminiProvider) return false
      if (!this.comparisonData?.products?.length) return false
      const hasAnyCitations = this.comparisonData.products.some((product) => {
        let reasoning = product.reasoning || {}
        if (typeof reasoning === 'string') {
          try {
            reasoning = JSON.parse(reasoning)
          } catch (e) {
            reasoning = {}
          }
        }
        if (!reasoning || typeof reasoning !== 'object') return false
        return Object.values(reasoning).some((val) => {
          if (Array.isArray(val)) {
            return val.some(
              (item) =>
                typeof item === 'object' &&
                item &&
                Array.isArray(item.citation_urls) &&
                item.citation_urls.length > 0
            )
          }
          if (typeof val === 'object' && val && Array.isArray(val.citation_urls) && val.citation_urls.length > 0) {
            return true
          }
          return false
        })
      })
      return !hasAnyCitations
    },
    queriesWithDetails() {
      if (!this.hasData) return []
      
      return this.filteredQueries.map(query => {
        // Get consideration metrics from consideration_metrics (full hash) or fallback to consideration (score)
        const considerationMetrics = query.scores?.consideration_metrics || query.scores?.consideration || {}
        // Handle both boolean and numeric (100/0) values for brand_alignment
        const brandAlignmentRaw = considerationMetrics.brand_alignment
        const brandAlignment = brandAlignmentRaw === true || brandAlignmentRaw === 100 || brandAlignmentRaw === 'true' || brandAlignmentRaw === '1'
        const sentiment = considerationMetrics.sentiment || 0
        
        return {
          ...query,
          brandAlignment,
          sentiment
        }
      })
    },
    queriesByPersona() {
      if (!this.hasData) return []
      
      // Group queries by persona (3 queries per persona) for Detailed Breakdown
      const groups = []
      const totalPersonas = Math.ceil(this.queries.length / 3)

      // Determine which persona indices to include based on filter
      const personaIndices =
        this.selectedPersona === 'all'
          ? Array.from({ length: totalPersonas }, (_, i) => i)
          : [parseInt(this.selectedPersona)]
      
      for (const i of personaIndices) {
        if (i < 0 || i >= totalPersonas) continue
        const startIndex = i * 3
        const endIndex = startIndex + 3
        const personaQueries = this.queries.slice(startIndex, endIndex)
        
        // Process queries with details
        const queriesWithDetails = personaQueries.map(query => {
          const considerationMetrics = query.scores?.consideration_metrics || query.scores?.consideration || {}
          const brandAlignmentRaw = considerationMetrics.brand_alignment
          const brandAlignment = brandAlignmentRaw === true || brandAlignmentRaw === 100 || brandAlignmentRaw === 'true' || brandAlignmentRaw === '1'
          const sentiment = considerationMetrics.sentiment || 0
          
          return {
            ...query,
            brandAlignment,
            sentiment
          }
        })
        
        groups.push({
          personaIndex: i,
          queries: queriesWithDetails
        })
      }
      
      return groups
    },
    // Accuracy Score = (# of GPT Responses Reflecting Value Props ÷ Total GPT Responses) × 100
    // Calculated only for the filtered queries (persona-specific when filtered)
    accuracyScore() {
      const filteredQueriesWithDetails = this.queriesWithDetails
      if (filteredQueriesWithDetails.length === 0) return 0
      const alignedCount = filteredQueriesWithDetails.filter(q => q.brandAlignment === true).length
      return (alignedCount / filteredQueriesWithDetails.length) * 100
    },
    // Sentiment Analysis: Tone Score = ((# of Positive Responses × 1) + (# of Neutral × 0) + (# of Negative × -1)) ÷ Total Responses
    // Then scaled to fit a 0–100 score: Tone of Summary Score = ((Tone Score + 1) ÷ 2) × 100
    // Calculated only for the filtered queries (persona-specific when filtered)
    toneScore() {
      const filteredQueriesWithDetails = this.queriesWithDetails
      if (filteredQueriesWithDetails.length === 0) return 0
      const sentimentSum = filteredQueriesWithDetails.reduce((sum, q) => {
        const sentimentValue = q.sentiment
        // Handle both integer (-1, 0, 1) and potentially stored as number
        return sum + (sentimentValue || 0)
      }, 0)
      return sentimentSum / filteredQueriesWithDetails.length
    },
    toneOfSummaryScore() {
      // Scale to 0-100: ((Tone Score + 1) ÷ 2) × 100
      const toneScore = this.toneScore
      return ((toneScore + 1) / 2) * 100
    },
    currentHistoricalData() {
      // When "All" personas are selected or per-persona data is unavailable, use overall series
      if (this.selectedPersona === 'all' || !this.historicalDataByPersona.length) {
        return this.historicalData
      }

      const personaIndex = parseInt(this.selectedPersona)
      if (Number.isNaN(personaIndex)) return this.historicalData

      const personaSeries = this.historicalDataByPersona.find(
        p => p.persona_index === personaIndex
      )

      return personaSeries ? personaSeries.data || [] : []
    },
    radarChartOptions() {
      if (!this.comparisonData || !this.comparisonData.products) {
        return {}
      }

      const products = this.comparisonData.products
      const metricsDescription = this.comparisonData.metrics_description || {}
      
      // Get all metric keys from the first product
      const metricKeys = products[0]?.metrics ? Object.keys(products[0].metrics) : []
      
      // Format metric names for display (replace underscores with spaces, capitalize)
      const categories = metricKeys.map(key => {
        return key.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
      })

      // Define a modern color palette
      const colorPalette = [
        '#1976D2', // Blue - Primary
        '#E57373', // Red
        '#81C784', // Green
        '#FFB74D', // Orange
        '#9575CD', // Purple
      ]

      // Find primary product index
      const primaryProductIndex = products.findIndex(p => p.is_primary)

      // Normalize scores to 1-10 for radar (legacy 1-5 data scaled up)
      const scale = this.attributeSentimentScoreScale
      const normalize = (v) => {
        if (v == null || typeof v !== 'number') return 0
        return scale === 5 ? v * 2 : v
      }
      // Prepare series data for each product
      const series = products.map((product, index) => {
        const isPrimary = product.is_primary
        // For primary product, show "Brand Name - Product Name"
        const displayName = isPrimary && this.primaryBrandName 
          ? `${this.primaryBrandName} - ${product.name}` 
          : product.name
        return {
          name: displayName,
          data: metricKeys.map(key => normalize(product.metrics[key])),
          pointPlacement: 'on',
          lineWidth: isPrimary ? 3 : 2,
          color: colorPalette[index % colorPalette.length],
          fillOpacity: isPrimary ? 0.15 : 0.05,
          marker: {
            radius: isPrimary ? 5 : 3,
            symbol: 'circle'
          },
          zIndex: isPrimary ? 10 : 1
        }
      })

      return {
        chart: {
          polar: true,
          type: 'area',
          height: 550,
          backgroundColor: 'transparent',
          spacing: [20, 20, 20, 20]
        },
        title: {
          text: null
        },
        pane: {
          size: '75%',
          background: {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderWidth: 1,
            borderColor: '#E0E0E0'
          }
        },
        xAxis: {
          categories: categories,
          tickmarkPlacement: 'on',
          lineWidth: 0,
          labels: {
            style: {
              fontSize: '13px',
              fontWeight: '500',
              color: '#424242'
            }
          }
        },
        yAxis: {
          gridLineInterpolation: 'polygon',
          lineWidth: 0,
          min: 0,
          max: 10,
          tickInterval: 1,
          labels: {
            enabled: false
          },
          gridLineColor: '#E0E0E0'
        },
        tooltip: {
          shared: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderWidth: 1,
          borderColor: '#E0E0E0',
          borderRadius: 8,
          shadow: true,
          useHTML: true,
          headerFormat: '<div style="font-size: 12px; font-weight: 600; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #E0E0E0">{point.key}</div>',
          pointFormat: '<div style="margin: 4px 0;"><span style="color:{series.color}; font-size: 20px;">●</span> <span style="font-weight: 500;">{series.name}:</span> <b style="color: {series.color};">{point.y}/10</b></div>',
          style: {
            fontSize: '13px'
          }
        },
        legend: {
          align: 'center',
          verticalAlign: 'bottom',
          layout: 'horizontal',
          itemStyle: {
            fontSize: '13px',
            fontWeight: '500',
            color: '#424242'
          },
          itemHoverStyle: {
            color: '#1976D2'
          },
          symbolRadius: 6,
          symbolHeight: 12,
          symbolWidth: 12,
          itemMarginBottom: 8
        },
        plotOptions: {
          area: {
            lineWidth: 2,
            marker: {
              lineWidth: 2,
              lineColor: '#FFFFFF'
            }
          }
        },
        series: series,
        credits: {
          enabled: false
        }
      }
    },
    attributeSentimentMetrics() {
      if (!this.comparisonData || !this.comparisonData.products || this.comparisonData.products.length === 0) {
        return []
      }
      
      // Get all metric keys from the first product
      const metricKeys = this.comparisonData.products[0]?.metrics ? Object.keys(this.comparisonData.products[0].metrics) : []
      
      // Format metric names for display (replace underscores with spaces, capitalize)
      return metricKeys.map(key => {
        const label = key.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
        
        return {
          key: key,
          label: label
        }
      })
    },
    attributeSentimentData() {
      if (!this.comparisonData || !this.comparisonData.products) {
        return []
      }
      
      // Return products with their metrics and reasoning
      return this.comparisonData.products.map(product => ({
        name: product.name,
        is_primary: product.is_primary || false,
        metrics: product.metrics || {},
        reasoning: product.reasoning || {}
      }))
    },
    // Detect if stored scores are 1-5 (legacy) or 1-10 for display normalization
    attributeSentimentScoreScale() {
      if (!this.comparisonData?.products?.length) return 10
      let maxScore = 0
      this.comparisonData.products.forEach(p => {
        const m = p.metrics || {}
        Object.values(m).forEach(v => {
          if (typeof v === 'number' && v > maxScore) maxScore = v
        })
      })
      return maxScore <= 5 ? 5 : 10
    },
    // Per-metric rank (1-based, 1 = best). Ties get same rank. Used for comparative mode.
    metricRanks() {
      if (!this.comparisonData?.products?.length) return {}
      const scale = this.attributeSentimentScoreScale
      const products = this.comparisonData.products
      const metricKeys = Object.keys(products[0]?.metrics || {})
      const ranks = {}
      metricKeys.forEach(metricKey => {
        const entries = products.map(p => ({
          name: p.name,
          score: this.normalizedScoreForScale((p.metrics || {})[metricKey], scale)
        }))
        entries.sort((a, b) => (b.score - a.score))
        let rank = 1
        entries.forEach((e, i) => {
          if (i > 0 && e.score < entries[i - 1].score) rank = i + 1
          if (!ranks[metricKey]) ranks[metricKey] = {}
          ranks[metricKey][e.name] = rank
        })
      })
      return ranks
    }
  },
  methods: {
    getAccuracyScoreColorClass(score) {
      // Color based on accuracy score (higher is better)
      if (score >= 70) return 'text-success'
      if (score >= 40) return 'text-info'
      if (score >= 20) return 'text-warning'
      return 'text-error'
    },
    getSentimentColorClass(score) {
      // Color based on sentiment score (higher is better, 0-100 scale)
      if (score >= 70) return 'text-success'
      if (score >= 40) return 'text-info'
      if (score >= 20) return 'text-warning'
      return 'text-error'
    },
    getSentimentLabel(sentiment) {
      // Convert sentiment value (-1, 0, 1) to label
      if (sentiment === 1) return 'Positive'
      if (sentiment === -1) return 'Negative'
      return 'Neutral'
    },
    getSentimentChipColor(sentiment) {
      // Color based on sentiment value
      if (sentiment === 1) return 'success'
      if (sentiment === -1) return 'error'
      return 'info'
    },
    getPersonaName(personaIndex) {
      if (this.personaProfiles && this.personaProfiles[personaIndex]) {
        return this.personaProfiles[personaIndex].name || `Persona ${personaIndex + 1}`
      }
      return `Persona ${personaIndex + 1}`
    },
    getProductReasoning(product, metricKey) {
      // Get reasoning array for a specific product and metric
      // Now returns objects with { text, citation_urls } structure
      if (!product.reasoning || !product.reasoning[metricKey]) {
        return []
      }
      const reasoning = product.reasoning[metricKey]
      if (!Array.isArray(reasoning)) return []
      
      // Handle both old string format and new object format for backward compatibility
      return reasoning.map(item => {
        if (typeof item === 'string') {
          return { text: item, citation_urls: [] }
        }
        return item
      })
    },
    toggleCitation(key) {
      this.expandedCitations = {
        ...this.expandedCitations,
        [key]: !this.expandedCitations[key]
      }
    },
    // Normalize raw score to 1-10 for threshold logic (legacy 1-5 data scaled up)
    normalizedScoreForScale(score, scale) {
      if (score == null || typeof score !== 'number') return 0
      return scale === 5 ? Math.round(score * 2) : score
    },
    // Sentiment band for matrix cell: positive (green), neutral (yellow), orange, negative (red)
    getSentimentBand(product, metricKey) {
      const scale = this.attributeSentimentScoreScale
      const raw = product.metrics?.[metricKey]
      const score = this.normalizedScoreForScale(raw, scale)
      if (this.sentimentMatrixMode === 'absolute') {
        // Absolute: use same good→bad palette as comparative (green, blue, yellow, orange, red)
        if (score >= 10) return 'positive' // green
        if (score >= 9) return 'neutral' // blue
        if (score >= 8) return 'yellow' // yellow
        if (score >= 7) return 'orange' // orange
        return 'negative' // red
      }
      // Comparative: by rank only — 1st green, 2nd blue, 3rd yellow, 4th orange, 5th+ red
      const rank = this.metricRanks[metricKey]?.[product.name] ?? 99
      if (rank === 1) return 'positive'
      if (rank === 2) return 'neutral'
      if (rank === 3) return 'yellow'
      if (rank === 4) return 'orange'
      return 'negative'
    },
    getCitationDetailsClass(product, metricKey) {
      const band = this.getSentimentBand(product, metricKey)
      return `citation-details-${band}`
    },
    truncateText(text, maxLength) {
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },
    getReasoningCardClass(product, metricKey) {
      const band = this.getSentimentBand(product, metricKey)
      return `reasoning-card-${band}`
    },
    getReasoningDotClass(product, metricKey) {
      const band = this.getSentimentBand(product, metricKey)
      return `reasoning-dot-${band}`
    },
    getMetricDisplayValue(product, metricKey) {
      const scale = this.attributeSentimentScoreScale
      const raw = product.metrics?.[metricKey]
      if (raw == null || typeof raw !== 'number') return null
      const normalized = this.normalizedScoreForScale(raw, scale)
      return normalized.toFixed(1)
    },
    // Free/nil/no-team users: blur the reasoning content in the bottom 3 competitor rows
    // of the matrix (brand name + cell colour stay visible). The primary brand is never blurred.
    shouldBlurMatrixRow(product, rowIndex) {
      if (!this.isFreeTierPlan) return false
      if (product.is_primary) return false
      return rowIndex >= this.attributeSentimentData.length - 3
    },
    formatHistoricalDate,
    onChartPointClick(pointIndex) {
      const point = this.currentHistoricalData[pointIndex]
      if (!point?.date || !isWithinHistoricalWindow(point.date)) return
      this.$emit('historical-date-selected', point.date)
    },
    async fetchHistoricalData() {
      if (!this.reviewEntityId) return
      
      this.loadingHistorical = true
      try {
        const params = this.selectedProvider ? { llm_provider: this.selectedProvider } : {}
        const response = await axios.get(
          `${BASE_URL}/review_entities/${this.reviewEntityId}/historical_sentiment`,
          {
            headers: { Authorization: localStorage.getItem('auth_token') },
            params
          }
        )
        
        if (response.data.success && response.data.data) {
          const payload = filterHistoricalPayload(response.data.data)
          this.historicalData = payload.historical_data
          this.historicalDataByPersona = payload.historical_data_by_persona
        }
      } catch (error) {
        console.error('Error fetching historical data:', error)
        this.historicalData = []
        this.historicalDataByPersona = []
      } finally {
        this.loadingHistorical = false
      }
    },
    async fetchComparisonData() {
      if (!this.assessmentId) return
      
      this.loadingComparison = true
      try {
        const params = this.selectedProvider ? { llm_provider: this.selectedProvider } : {}
        
        const comparisonResponse = await axios.get(
          `${BASE_URL}/assessments/${this.assessmentId}/comparison`,
          {
            headers: { Authorization: localStorage.getItem('auth_token') },
            params: params
          }
        )
        
        if (comparisonResponse.data.success && comparisonResponse.data.comparison) {
          // competitor_data now includes citation_urls embedded in each reasoning item
          this.comparisonData = comparisonResponse.data.comparison.competitor_data
        } else {
          this.comparisonData = null
        }
      } catch (error) {
        console.error('Error fetching comparison data:', error)
        this.comparisonData = null
      } finally {
        this.loadingComparison = false
      }
    },
    updateChart() {
      if (this.currentHistoricalData.length <= 1) return
      
      const component = this
      const chartData = this.currentHistoricalData.map((d) => {
        const isSelected = d.date === this.selectedHistoricalDate
        return {
          x: new Date(d.date).getTime(),
          y: d.sentiment,
          marker: {
            enabled: isSelected,
            radius: isSelected ? 6 : 4,
            fillColor: '#4caf50',
            lineColor: '#ffffff',
            lineWidth: 2
          }
        }
      })
      
      const chartContainer = document.getElementById('sentimentChart')
      if (!chartContainer) return
      
      Highcharts.chart('sentimentChart', {
        chart: {
          type: 'spline',
          height: 300,
          backgroundColor: '#ffffff',
          spacing: [20, 20, 20, 20]
        },
        title: {
          text: null
        },
        xAxis: {
          type: 'datetime',
          title: {
            enabled: false
          },
          gridLineColor: '#e0e0e0',
          lineColor: '#e0e0e0',
          tickColor: '#e0e0e0',
          labels: {
            style: {
              color: '#666666'
            }
          }
        },
        yAxis: {
          title: {
            enabled: false
          },
          min: 0,
          max: 100,
          tickPositions: [0, 25, 50, 75, 100],
          gridLineColor: '#e0e0e0',
          lineColor: '#e0e0e0',
          tickColor: '#e0e0e0',
          labels: {
            style: {
              color: '#666666',
              fontSize: '20px'
            },
            formatter: function() {
              const value = this.value
              if (value === 25) {
                return '<span style="font-size: 32px;">☹️</span>'
              } else if (value === 50) {
                return '<span style="font-size: 32px;">😐</span>'
              } else if (value === 75) {
                return '<span style="font-size: 32px;">😃</span>'
              } else if (value === 100) {
                return '<span style="font-size: 32px;">😍</span>'
              }
              return ''
            },
            useHTML: true
          }
        },
        tooltip: {
          backgroundColor: '#ffffff',
          borderColor: '#e0e0e0',
          borderRadius: 4,
          shadow: false,
          style: {
            color: '#333333'
          },
          formatter: function() {
            const dateStr = Highcharts.dateFormat('%Y-%m-%d', this.x)
            return `${dateStr}<br/><b>${this.y.toFixed(1)}</b><br/><span style="font-size: 11px; color: #666;">Click to view this date</span>`
          }
        },
        plotOptions: {
          spline: {
            cursor: 'pointer',
            marker: {
              enabled: false,
              radius: 4,
              fillColor: '#4caf50',
              lineColor: '#ffffff',
              lineWidth: 2,
              states: {
                hover: {
                  enabled: true
                }
              }
            },
            lineWidth: 5,
            fillOpacity: 0,
            states: {
              hover: {
                lineWidth: 5
              }
            },
            point: {
              events: {
                click: function() {
                  component.onChartPointClick(this.index)
                }
              }
            }
          }
        },
        series: [{
          name: 'Sentiment',
          data: chartData,
          color: '#4caf50'
        }],
        credits: {
          enabled: false
        },
        legend: {
          enabled: false
        }
      })
    }
  },
  watch: {
    reviewEntityId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchHistoricalData()
        }
      }
    },
    assessmentId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchComparisonData()
        }
      }
    },
    selectedProvider: {
      handler() {
        if (this.assessmentId) {
          this.fetchComparisonData()
        }
        if (this.reviewEntityId) {
          this.fetchHistoricalData()
        }
      }
    },
    historicalData: {
      handler() {
        this.$nextTick(() => {
          this.updateChart()
        })
      }
    },
    selectedPersona: {
      handler() {
        this.$nextTick(() => {
          this.updateChart()
        })
      }
    },
    selectedHistoricalDate: {
      handler() {
        this.$nextTick(() => {
          this.updateChart()
        })
      }
    }
  }
}
</script>

<style scoped>
.metric-card {
  border-radius: 16px;
  border-color: var(--brand-ocean-100) !important;
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 30, 53, 0.12);
}

.persona-header-row {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.historical-chart-card {
  border-radius: 16px;
  border-color: var(--brand-sand-300) !important;
}

.matrix-legend {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.matrix-legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}

.matrix-legend-label {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}

.matrix-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.matrix-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
}

.matrix-legend-swatch-positive {
  background-color: var(--brand-teal-500);
}

.matrix-legend-swatch-neutral {
  background-color: var(--brand-ocean-600);
}

.matrix-legend-swatch-yellow {
  background-color: #facc15;
}

.matrix-legend-swatch-orange {
  background-color: var(--brand-amber-400);
}

.matrix-legend-swatch-negative {
  background-color: #f44336;
}

.attribute-sentiment-table-container {
  overflow-x: auto;
  width: 100%;
}

.attribute-sentiment-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.attribute-sentiment-table thead {
  background-color: var(--brand-sand-100);
  position: sticky;
  top: 0;
  z-index: 10;
}

.attribute-sentiment-table th {
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: var(--brand-ink-900);
  border-bottom: 2px solid var(--brand-sand-300);
  white-space: nowrap;
}

.attribute-sentiment-table .brand-column {
  min-width: 200px;
  position: sticky;
  left: 0;
  background-color: var(--brand-sand-100);
  z-index: 5;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
}

.attribute-sentiment-table .metric-column {
  min-width: 180px;
  text-align: center;
}

.attribute-sentiment-table tbody tr {
  border-bottom: 1px solid var(--brand-sand-300);
}

.attribute-sentiment-table tbody tr:hover {
  background-color: var(--brand-sand-50);
}

.attribute-sentiment-table td {
  padding: 16px 12px;
  vertical-align: top;
}

.attribute-sentiment-table .brand-cell {
  position: sticky;
  left: 0;
  background-color: #ffffff;
  z-index: 4;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  font-weight: 500;
}

.attribute-sentiment-table tbody tr:hover .brand-cell {
  background-color: #fafafa;
}

/* Primary product row highlighting */
.primary-product-row {
  background-color: var(--brand-ocean-100);
}

.primary-product-row:hover {
  background-color: #edf3fa !important;
}

.primary-brand-cell {
  background-color: var(--brand-ocean-100) !important;
  border-left: 3px solid var(--brand-ocean-600);
}

.primary-product-row:hover .primary-brand-cell {
  background-color: #e4ecf7 !important;
}

.reasoning-cell {
  background-color: transparent;
  padding: 12px;
}

.reasoning-items-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reasoning-card {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  padding-bottom: 18px;
  border-radius: 8px;
  border: 2px solid;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s, transform 0.2s;
  position: relative;
}

.reasoning-card-metric-value {
  position: absolute;
  bottom: 4px;
  right: 10px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1;
}

.reasoning-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.reasoning-card-positive {
  border-color: #4caf50;
  background-color: rgba(76, 175, 80, 0.05);
}

.reasoning-card-positive:hover {
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.reasoning-card-neutral {
  border-color: #1976d2;
  background-color: rgba(25, 118, 210, 0.05);
}

.reasoning-card-neutral:hover {
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.reasoning-card-yellow {
  border-color: #facc15;
  background-color: rgba(250, 204, 21, 0.08);
}

.reasoning-card-yellow:hover {
  box-shadow: 0 4px 12px rgba(250, 204, 21, 0.35);
}

.reasoning-card-orange {
  border-color: #ff9800;
  background-color: rgba(255, 152, 0, 0.05);
}

.reasoning-card-orange:hover {
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
}

.reasoning-card-negative {
  border-color: #f44336;
  background-color: rgba(244, 67, 54, 0.05);
}

.reasoning-card-negative:hover {
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

.reasoning-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
}

.reasoning-text {
  flex: 1;
  line-height: 1.4;
  font-size: 13px;
}

/* Free/nil/no-team paywall blur — bottom 3 matrix rows. The blur itself lives on
   the .reasoning-text / .reasoning-card-metric-value spans via .brand-paywall-blur
   so the card colour and brand name stay legible; here we only block the
   citation-expand click on those rows. */
.matrix-row-blurred .reasoning-card {
  pointer-events: none;
}

.reasoning-card-grounded {
  position: relative;
}

.reasoning-card-wrapper {
  margin-bottom: 8px;
}

.reasoning-card-wrapper:last-child {
  margin-bottom: 0;
}

.reasoning-card-grounded {
  cursor: pointer;
}

.reasoning-card-grounded:hover {
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
}

.reasoning-grounded-icon {
  opacity: 0.7;
  flex-shrink: 0;
  transition: opacity 0.2s, transform 0.2s;
}

.reasoning-card:hover .reasoning-grounded-icon {
  opacity: 1;
}

.citation-details {
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 10px 12px;
  margin-top: -4px;
  animation: slideDown 0.2s ease-out;
}

.citation-details-positive {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
  border-top: none;
}

.citation-details-positive .citation-header {
  color: #166534;
}

.citation-details-neutral {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  border-top: none;
}

.citation-details-neutral .citation-header {
  color: #1d4ed8;
}

.citation-details-yellow {
  background: linear-gradient(135deg, #fefce8 0%, #fef08a 100%);
  border: 1px solid #facc15;
  border-top: none;
}

.citation-details-yellow .citation-header {
  color: #854d0e;
}

.citation-details-orange {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 1px solid #fdba74;
  border-top: none;
}

.citation-details-orange .citation-header {
  color: #9a3412;
}

.citation-details-negative {
  background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
  border: 1px solid #fca5a5;
  border-top: none;
}

.citation-details-negative .citation-header {
  color: #991b1b;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.citation-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 500;
}

.citation-snippet {
  display: flex;
  align-items: flex-start;
  font-size: 11px;
  font-style: italic;
  color: #555;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 6px;
  border-left: 2px solid #1976d2;
  line-height: 1.4;
  margin-bottom: 8px;
}

.citation-snippet .v-icon {
  flex-shrink: 0;
  opacity: 0.6;
  margin-top: 1px;
}

.citation-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.citation-source-link {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  font-size: 11px;
  color: #1976d2;
  text-decoration: none;
  transition: all 0.2s;
  max-width: 100%;
  width: fit-content;
}

.citation-source-link:hover {
  background: white;
  border-color: #1976d2;
  box-shadow: 0 2px 4px rgba(25, 118, 210, 0.15);
}

.reasoning-dot-positive {
  background-color: #4caf50;
}

.reasoning-dot-neutral {
  background-color: #1976d2;
}

.reasoning-dot-yellow {
  background-color: #facc15;
}

.reasoning-dot-orange {
  background-color: #ff9800;
}

.reasoning-dot-negative {
  background-color: #f44336;
}
</style>

